import type { TypedDocumentNode } from '@graphql-typed-document-node/core'
import { ClientError, GraphQLClient } from 'graphql-request'

import { type ApiMode, endpointFor } from './config'

export type RequestSession = {
  mode: ApiMode
  token?: string | null
}

const clients = new Map<string, GraphQLClient>()

function clientFor(mode: ApiMode): GraphQLClient {
  const url = endpointFor(mode)
  let client = clients.get(url)
  if (!client) {
    client = new GraphQLClient(url)
    clients.set(url, client)
  }
  return client
}

/**
 * Called whenever the API rejects our credentials (expired/invalid token), so
 * the stored session can be cleared instead of leaving the screen erroring.
 */
let unauthenticatedHandler: (() => void) | null = null

export function setUnauthenticatedHandler(handler: (() => void) | null) {
  unauthenticatedHandler = handler
}

/** Runs a generated (typed) GraphQL document against the selected environment. */
export async function gqlRequest<TResult, TVariables>(session: RequestSession, document: TypedDocumentNode<TResult, TVariables>, variables?: TVariables): Promise<TResult> {
  const headers: Record<string, string> = {}
  if (session.token) headers.Authorization = `Bearer ${session.token}`

  try {
    return await clientFor(session.mode).request<TResult>(document, variables as Record<string, unknown> | undefined, headers)
  } catch (error) {
    if (session.token && isUnauthenticated(error)) unauthenticatedHandler?.()
    throw error
  }
}

/** True for expired/invalid credentials — the session must be discarded. */
export function isUnauthenticated(error: unknown): boolean {
  if (!(error instanceof ClientError)) return false
  if (error.response.status === 401) return true
  return (error.response.errors ?? []).some((graphQLError) => {
    const code = (graphQLError.extensions as { code?: unknown } | undefined)?.code
    if (code === 'UNAUTHENTICATED') return true
    return /expired token|invalid token|not authenticated/i.test(graphQLError.message ?? '')
  })
}

export function graphqlErrorMessage(error: unknown): string {
  if (error instanceof ClientError) {
    const first = error.response.errors?.[0]?.message
    if (first) return first
    if (error.response.status === 401) return 'Invalid username or password.'
  }
  if (error instanceof Error && error.message) return error.message
  return 'Something went wrong. Please try again.'
}

export function isUnauthorized(error: unknown): boolean {
  return error instanceof ClientError && error.response.status === 401
}
